import * as React from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from "../ui/avatar"
import { Button } from "../ui/button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuShortcut,
    DropdownMenuTrigger,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuPortal,
} from "../../components/ui/dropdown-menu"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../ui/alert-dialog"
import { useAuth } from "../../context/AuthContext"
import { LogOut, User, Settings, CreditCard, Camera, Languages } from "lucide-react"
import { toast } from "../../hooks/use-toast"
import { authApi } from "../../api/auth.api";
import { clientsApi } from "../../api/clients.api";
import { ImageUploadModal } from "../modals/ImageUploadModal";

export function UserNav() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [showLogoutDialog, setShowLogoutDialog] = React.useState(false);
    const [imageModalOpen, setImageModalOpen] = React.useState(false);

    // Generate initials from username or email
    const getInitials = () => {
        if (!user) return "??";
        if (user.username) {
            return user.username.slice(0, 2).toUpperCase();
        }
        if (user.email) {
            return user.email.slice(0, 2).toUpperCase();
        }
        return "??";
    };

    const handleLogoutClick = () => {
        setShowLogoutDialog(true);
    };

    const handleLogoutConfirm = () => {
        toast({
            title: t('common:buttons.logout'),
            description: t('common:languageChanged'), // Fallback or search for "Logout success"
            type: "success",
        });
        // Small delay so toast is visible
        setTimeout(() => {
            logout();
        }, 500);
    };

    const handlePhotoUpdate = async (newImage: string) => {
        try {
            if (user?.role === 'Admin') {
                // Admin profile is managed in auth-service
                const { imagePath } = await authApi.uploadProfileImage(newImage);
                await authApi.updateProfile({ image: imagePath });
            } else {
                // Client/Agent profile is managed in client-service (which also handles R2 upload)
                await clientsApi.updateMyProfile({ imageUrl: newImage });
            }

            toast({
                title: t('common:success'),
                description: t('settings:profile.success.imageUpdated'),
                type: "success",
            });
            // Force reload to update context for now
            window.location.reload();
        } catch (error) {
            console.error("Failed to update photo", error);
            toast({
                title: t('common:error'),
                description: t('settings:profile.error.upload'),
                type: "error",
            });
        }
    };

    const changeLanguage = async (lng: string) => {
        console.log("UserNav: Changing language to", lng);
        await i18n.changeLanguage(lng);
        toast({
            title: t('common:success'),
            description: t('common:languageChanged'),
            type: "success",
        });
    };


    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                        <Avatar className="h-8 w-8 transition-transform hover:scale-105 cursor-pointer">
                            <AvatarImage src={user?.image || undefined} alt={user?.username || "User"} />
                            <AvatarFallback className="bg-primary text-primary-foreground">
                                {getInitials()}
                            </AvatarFallback>
                        </Avatar>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                    <DropdownMenuLabel className="font-normal">
                        <div className="flex flex-col space-y-1">
                            <p className="text-sm font-medium leading-none">
                                {user?.username || t('common:user')}
                            </p>
                            <p className="text-xs leading-none text-muted-foreground">
                                {user?.email || ""}
                            </p>
                        </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                        <DropdownMenuItem onClick={() => {
                            console.log("Opening image modal");
                            setImageModalOpen(true);
                        }}>
                            <Camera className="mr-2 h-4 w-4" />
                            {t('settings:updateImage')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/settings/profile')}>
                            <User className="mr-2 h-4 w-4" />
                            {t('settings:accountSettings')}
                            <DropdownMenuShortcut>⇧⌘P</DropdownMenuShortcut>
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                            <CreditCard className="mr-2 h-4 w-4" />
                            {t('common:labels.account')}
                            <DropdownMenuShortcut>⌘B</DropdownMenuShortcut>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/settings/security')}>
                            <Settings className="mr-2 h-4 w-4" />
                            {t('settings:title')}
                            <DropdownMenuShortcut>⌘S</DropdownMenuShortcut>
                        </DropdownMenuItem>
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                        <DropdownMenuSub>
                            <DropdownMenuSubTrigger>
                                <Languages className="mr-2 h-4 w-4" />
                                <span>{t('settings:language')}</span>
                            </DropdownMenuSubTrigger>
                            <DropdownMenuPortal>
                                <DropdownMenuSubContent>
                                    <DropdownMenuItem onClick={() => changeLanguage('ht')}>
                                        Kreyòl Ayisyen {i18n.language === 'ht' && "✓"}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => changeLanguage('fr')}>
                                        Français {i18n.language === 'fr' && "✓"}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => changeLanguage('en')}>
                                        English {i18n.language === 'en' && "✓"}
                                    </DropdownMenuItem>
                                </DropdownMenuSubContent>
                            </DropdownMenuPortal>
                        </DropdownMenuSub>
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        onClick={handleLogoutClick}
                        className="text-red-600 focus:text-red-600 focus:bg-red-50"
                    >
                        <LogOut className="mr-2 h-4 w-4" />
                        {t('common:buttons.logout')}
                        <DropdownMenuShortcut>⇧⌘Q</DropdownMenuShortcut>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <ImageUploadModal
                isOpen={imageModalOpen}
                onClose={() => setImageModalOpen(false)}
                onUpload={handlePhotoUpdate}
            />

            {/* Logout Confirmation Dialog */}
            <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('common:buttons.logout')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('settings:resetPinConfirmMessage', { email: '' }).split('.')[1] || "Are you sure you want to log out?"}
                            {/* Adjusted since common doesn't have logout confirm yet, or use a new key */}
                            {t('common:buttons.confirm')}?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('common:buttons.cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={handleLogoutConfirm}>
                            {t('common:buttons.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    )
}


